import isArray from "lodash/isArray";
import isObject from "lodash/isObject";
import isEmpty from "lodash/isEmpty";
import moment from "../../../../moment";
import config from "../../../../config";

/**
 * - Event date/time formatting (UTC when both date and time are set, otherwise local).
 * - Anonymous reporter normalization.
 * - Root causes mapping to id arrays.
 * - First risk analysis file ids.
 * - Second risk analysis conditional inclusion based on risk level.
 * - Removal of top-level sections whose every value is null/undefined/empty.
 *
 * @param {object}   values          - Raw form field values from Ant Design.
 * @param {Function} formatUTC       - UTC-aware date formatter.
 * @param {Function} format          - Local date formatter.
 * @param {Array}    rootCauses      - Selected root-cause objects (mapped to ids).
 * @returns {object} Cleaned API payload.
 */
export const prepareFormData = (values, formatUTC, format, rootCauses = []) => {
  const { basic_data, investigation, first_risk_analysis, second_risk_analysis, management_summary } = values;

  const event_date = basic_data?.event_date ? moment(basic_data.event_date).format(config.defaultDateFormat) : "";
  const event_time = basic_data?.event_time ? moment(basic_data.event_time).format("HH:mm") : "";
  const eventFullDate = event_date + " " + event_time;

  let prepareDataForAnonymousReport = {};
  if (basic_data?.reporter_id === "Anonymous") {
    prepareDataForAnonymousReport = {
      reporter_id: null,
      reporter_name: "Anonymous",
      reporter_type: "anonymous"
    };
  }

  const formData = {
    basic_data: {
      ...basic_data,
      ...prepareDataForAnonymousReport,
      files: values?.basic_files?.map((item) => item.id) || [],
      event_date: (() => {
        if (!basic_data?.event_date) return null;
        const formatter = event_time ? formatUTC : format;
        return formatter({ date: eventFullDate, dateFormat: config.defaultDateFormat, defaultReturn: null });
      })(),
      event_time: basic_data?.event_time ? moment(basic_data.event_time).format("HH:mm") : null,
      report_date: basic_data?.report_date
        ? format({ date: basic_data?.report_date, dateFormat: config.defaultDateFormat, defaultReturn: null })
        : null
    },
    investigation: {
      ...investigation,
      deadline: format({ date: investigation.deadline, dateFormat: config.defaultDateFormat, defaultReturn: null }),
      tag_ids: investigation.tags
    },
    root_causes: rootCauses ? rootCauses.map((item) => item.id) : [],
    first_risk_analysis: {
      ...first_risk_analysis,
      first_analysis_deadline: format({
        date: first_risk_analysis?.first_analysis_deadline,
        dateFormat: config.defaultDateFormat,
        defaultReturn: null
      }),
      first_analysis_files: values?.first_files?.map((item) => item.id)
    },
    management_summary,
    ...(values?.riskAnalysis?.riskLevel?.corrective_action_is_required && { second_risk_analysis })
  };

  Object.keys(formData).forEach((item) => {
    if (
      Object.values(formData[item] || {}).every(
        (field) => field === null || field === undefined || ((isArray(field) || isObject(field)) && isEmpty(field))
      )
    ) {
      delete formData[item];
    }
  });

  return formData;
};
